import * as React from 'react';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { UploadIcon } from 'tdesign-icons-react';
import {
  Button,
  Loading,
  MessagePlugin,
  Popup,
  RequestMethodResponse,
  Tooltip,
  Upload,
} from 'tdesign-react';

import { MediaConfigType } from '@/const/richMedia';
import { LibraryObjectData } from '@/interface/library';
import { batchUploadCos, BatchUploadStatus, BatchUploadType } from '@/material/form-upload/service';
import { CgiErrorCode } from '@/services/cgi';
import { GetListResp, insertLibrary } from '@/services/library';

import styles from './index.module.less';

export interface UploadFileProps {
  value?: File[];
  searchValue?: LibraryObjectData;
  disabled?: boolean;
  alertRender?: React.ReactNode;
  btnText?: string;
  showSizeMsg?: string;
  limit: {
    fileType?: string[];
    size?: number;
    count?: number;
    uploadType?: string[];
    type: BatchUploadType,
  };
  dialogConfig?: any;
  multiple?: boolean;
  maxCount?: number;
  isShowRichMedia?: boolean;
  width?: number | string;
  height?: number | string;
  wrapStyle?: any;
  richMedia?: { type: MediaConfigType };
  onChange: (value: GetListResp['userResourceList']) => any;
}

const maxCount = 30; // 最大上传数量

const fail = {
  status: 'fail',
  response: { url: '' },
} as unknown as Promise<RequestMethodResponse>;

const success = {
  status: 'success',
  response: {},
} as unknown as Promise<RequestMethodResponse>;

export const LibraryUpload: React.FC<UploadFileProps> = (props) => {
  const {
    onChange,
    width: wrapWidth = 100,
    disabled = false,
    limit,
    value = [],
    searchValue,
  } = props;
  const [searchParams] = useSearchParams();
  const storyId = searchParams.get('id') || '';
  const [uploadInfo, setUploadInfo] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (value: unknown[]) => {
    onChange(value as GetListResp['userResourceList']);
  };

  const uploadFile = async (file: any) => {
    setLoading(true);

    await batchUploadCos(
      file,
      { storyId, size: limit?.size || 10, type: limit?.type },
      (info: string) => {
        setUploadInfo(info);
      },
      async (result: {
        code: BatchUploadStatus,
        msg: string,
        urls: { resourceName: string, url: string }[]
      }) => {
        const { code, msg, urls } = result;
        const postReqData = {
          materialId: searchValue?.materialId,
          type: searchValue?.type,
          tabType: searchValue?.tabType,
        };

        if (code === BatchUploadStatus.fail) {
          MessagePlugin.error(msg || '全部上传失败,请重试');
          setUploadInfo('');
          setLoading(false);
          return fail;
        }

        if (code === BatchUploadStatus.partFail) {
          const res = await insertLibrary({
            ...postReqData,
            batchResource: urls,
          });
          setUploadInfo('');
          setLoading(false);
          if (res?.code === CgiErrorCode.NO_ERROR) {
            handleChange(res?.data?.list || []);
            MessagePlugin.error(msg || '部分上传失败,请重试');
            return success;
          }
          if (res?.code === CgiErrorCode.MoveMaterialPartialFail) {
            handleChange(res?.data?.list || []);
            MessagePlugin.error(res?.message || '部分上传失败,请重试');
            return success;
          }
          MessagePlugin.error('上传失败,请重试');
          return fail;
        }

        if (code === BatchUploadStatus.success) {
          const res = await insertLibrary({
            ...postReqData,
            batchResource: urls,
          });
          setUploadInfo('');
          setLoading(false);
          if (res?.code === CgiErrorCode.NO_ERROR) {
            handleChange(res?.data?.list || []);
            MessagePlugin.success('上传成功');
            return success;
          }
          if (res?.code === CgiErrorCode.MoveMaterialPartialFail) {
            handleChange(res?.data?.list || []);
            MessagePlugin.warning(res?.message || '部分上传成功');
            return success;
          }
          MessagePlugin.error(res?.message || '上传失败,请重试');
          return fail;
        }
      },
    );

    return success;
  };

  const uploadChange = (files: any) => {
    const allowUploadFiles: any[] = [];
    const notAllowUploadFiles: any[] = [];
    files.forEach((file: any, index: number) => {
      if (index <= maxCount - 1) {
        allowUploadFiles.push(file);
      } else {
        notAllowUploadFiles.push(file);
      }
    });
    if (notAllowUploadFiles.length > 0) {
      const names = notAllowUploadFiles.map((item: any) => item?.name).join('、');
      MessagePlugin.warning(`最多上传${maxCount}个文件，${names}超出限制，请重新上传`);
    }
  };

  const isType = (val: string, name: string) => {
    const isPass = Boolean(limit?.uploadType?.includes(val));
    if (!isPass) {
      MessagePlugin.error(`【${name}】不符合格式：${limit?.fileType?.join('、')}`);
    }
    return isPass;
  };

  const isSize = (val: number, name: string) => {
    const isPass = val <= (limit?.size || 10) * 1024 * 1024;
    if (!isPass) {
      MessagePlugin.error(`【${name}】大小超过${limit?.size}MB`);
    }
    return isPass;
  };

  const beforeUpload = async (file: any): Promise<boolean> => {
    const validType = isType(file.type, file?.name);
    const validSize = isSize(file.size, file?.name);
    return validType && validSize;
  };

  const getNormalDom = () => (
    <>
      <Upload
        disabled={disabled || loading}
        onSelectChange={uploadChange}
        className={styles.upload}
        style={{ width: wrapWidth }}
        max={limit?.count || maxCount}
        multiple={true}
        requestMethod={uploadFile}
        uploadAllFilesInOneRequest={true}
        showImageFileName={false}
        allowUploadDuplicateFile={false}
        files={value}
        beforeUpload={beforeUpload}
        fileListDisplay={fileList => (
          <Popup
            visible={fileList?.files?.length > 0}
            placement='bottom'
            showArrow content={
              <div>
                {
                  fileList?.files?.map((e, i) => (
                    <div key={i} className={styles.fileListDisplay}>
                      <div className={styles.fileListName}>{e.name}</div>
                      <div className={styles.fileListPercent}><Loading size='small' /></div>
                    </div>
                  ))
                }
              </div>}>
            <div style={{ width: 88 }}></div>
          </Popup>
        )}
      >
        <Tooltip
          content={`单次最多上传${limit?.count || maxCount}个文件`}
          showArrow
          theme="default"
        >
          <Button theme="default" icon={<UploadIcon />} style={{ width: 88 }}>
            上传
            <span className={styles.info}>{uploadInfo}</span>
          </Button>
        </Tooltip>
      </Upload>
    </>
  );

  return <div>{getNormalDom()}</div>;
};

export default LibraryUpload;
